import type { Identifier } from "react-admin";
import {
  type Claimant,
  type Policy,
  type StoplossClaim,
  type AggregateBenefit,
  type SpecificBenefit,
  type CommonFields,
  type AuditFields,
  type ClaimRef,
  type ClaimantRef,
  type PolicyRef,
  type ClientRef,
  type PartnerRef,
  type DateYMDString,
  CommonStatus,
} from "@/types";

export const ReimbursementStatus = {
  PENDING: CommonStatus.PENDING,
  APPROVED: CommonStatus.APPROVED,
  DENIED: CommonStatus.DENIED,
  PAID: CommonStatus.PAID,
  ADJUSTED: "adjusted",
  UNDER_REVIEW: "under_review",
} as const;

export type ReimbursementStatusType = (typeof ReimbursementStatus)[keyof typeof ReimbursementStatus];

/** Authoritative payment method (BE `payment_method`), unified across all payment types. */
export const PaymentMethod = {
  CHECK: "check",
  ACH: "ach",
  EFT: "eft",
  WIRE: "wire",
} as const;

export type PaymentMethodType = (typeof PaymentMethod)[keyof typeof PaymentMethod];

/**
 * Base reimbursement interface - fields that are in both create and read
 */
interface ReimbursementBase extends ClaimRef, ClaimantRef, PolicyRef, ClientRef, PartnerRef {
  // References - matching Go's reference fields
  aggregate_deductible_id_ref?: Identifier;
  specific_deductible_id_ref?: Identifier;

  // Financial Amounts
  adjustment_amount?: number;
  pended_amount?: number;
  reimbursement_amount?: number;
  denial_amount?: number;
  requested_amount: number; // Required by backend validation

  // Reason Fields
  adjustment_reason?: string;
  pending_reason?: string;
  denial_reason?: string;

  // Payment details — authoritative across all methods (check/ach/eft/wire). Prefer
  // these over the legacy check_* / eft fields below.
  payment_method?: PaymentMethodType;
  payment_reference?: string; // check number or ACH/wire transaction reference
  payment_date?: DateYMDString | null; // date of payment, unified across methods

  // Dates
  received_date?: DateYMDString | null;
  check_date?: DateYMDString | null; // legacy — prefer payment_date
  service_start_date?: DateYMDString | null;
  service_end_date?: DateYMDString | null;
  pended_audit_date?: DateYMDString | null;
  adjustment_audit_date?: DateYMDString | null;

  // Status and Processing
  status: ReimbursementStatusType; // Required by backend validation

  // Legacy payment fields — prefer payment_method / payment_date. Retained for rows that
  // predate them; read these only as a fallback when the authoritative fields are unset.
  check_number?: string;
  eft?: boolean;

  // Administrative Fields
  claims_administrator?: string;
  payee?: string;
  provider?: string;
  auditor_name?: string;

  source_documents?: Identifier[];
}

/**
 * Full reimbursement record from the API - includes server-managed fields
 */
export interface Reimbursement extends ReimbursementBase, CommonFields, AuditFields {
  // Stop-loss claim this payment was linked to at load (nullable — the linker is
  // fail-open). Joins to the Aegle Accel funded date for the reimbursement exhibit (#5310).
  stoploss_claim_id_ref?: string;

  // Expanded/populated fields (read-only, populated by backend)
  claimant?: Claimant;
  policy?: Policy;
  claim?: StoplossClaim;
  aggregate_deductible?: AggregateBenefit;
  specific_deductible?: SpecificBenefit;
}

/**
 * Payload for creating a new reimbursement
 */
export interface ReimbursementCreate extends ReimbursementBase {
  // Expanded fields are read-only and should not be sent in create payload
}

/**
 * Payload for updating a reimbursement - all fields optional except id
 */
export interface ReimbursementUpdate extends Partial<ReimbursementBase> {
  id: Identifier;
  // Required fields from ReimbursementBase become optional in updates
  requested_amount?: number;
  status?: ReimbursementStatusType;
}
